/**
 * Streaming pitch tracker for one singing voice, using YIN (de Cheveigné &
 * Kawahara, 2002). Feed it mic samples as they arrive; it low-passes and
 * decimates them to about 16 kHz and emits one frame every 10 ms.
 *
 * A frame is { sample, f0, clarity, rms }: `sample` is the absolute input
 * sample index at the centre of the analysed span (so the caller can place
 * it on the audio clock), `f0` the fundamental in Hz, `clarity` how periodic
 * the span is (1 - YIN's aperiodicity; a sung vowel is well above 0.8, noise
 * and breath are low) and `rms` its level. Whether a frame counts as sung
 * is the caller's decision.
 */

const MIN_HZ = 60; // below a bass's low C
const MAX_HZ = 1200; // above a soprano's high C
const TARGET_RATE = 16000;
export const HOP_SECONDS = 0.01;
const WINDOW_SECONDS = 0.032;
const YIN_THRESHOLD = 0.15;
const FIR_TAPS = 31;
// Gaps in the input up to this long are filled with silence, not restarted
const MAX_FILL_SECONDS = 0.04;

// Windowed-sinc low-pass just under the decimated Nyquist frequency
function lowPass(factor) {
  if (factor <= 1) return Float32Array.of(1);
  const cutoff = 0.45 / factor; // cycles per input sample
  const middle = (FIR_TAPS - 1) / 2;
  const taps = new Float32Array(FIR_TAPS);
  let sum = 0;
  for (let i = 0; i < FIR_TAPS; i++) {
    const x = i - middle;
    const sinc = x === 0 ? 2 * cutoff : Math.sin(2 * Math.PI * cutoff * x) / (Math.PI * x);
    const hamming = 0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (FIR_TAPS - 1));
    taps[i] = sinc * hamming;
    sum += taps[i];
  }
  for (let i = 0; i < FIR_TAPS; i++) taps[i] /= sum;
  return taps;
}

// Appends to a Float32Array, growing it geometrically
function append(buffer, length, values, count = values.length) {
  if (length + count > buffer.length) {
    const grown = new Float32Array(Math.max(length + count, buffer.length * 2));
    grown.set(buffer.subarray(0, length));
    buffer = grown;
  }
  buffer.set(count === values.length ? values : values.subarray(0, count), length);
  return buffer;
}

export function createPitchTracker(sampleRate) {
  const factor = Math.max(1, Math.round(sampleRate / TARGET_RATE));
  const rate = sampleRate / factor;
  const tauMin = Math.max(2, Math.floor(rate / MAX_HZ));
  const tauMax = Math.ceil(rate / MIN_HZ);
  const windowSize = Math.round(WINDOW_SECONDS * rate);
  const hop = Math.max(1, Math.round(HOP_SECONDS * rate));
  const span = windowSize + tauMax;
  const maxFill = Math.round(MAX_FILL_SECONDS * sampleRate);
  const taps = lowPass(factor);
  const half = (taps.length - 1) / 2;

  const diff = new Float64Array(tauMax + 1);
  const cmnd = new Float64Array(tauMax + 1);

  let raw = new Float32Array(8192);
  let rawLength = 0;
  let rawStart = 0; // absolute input index of raw[0]
  let nextDecimated = 0; // decimated index produced next (absolute)
  let decimated = new Float32Array(8192);
  let decimatedLength = 0;
  let decimatedStart = 0; // absolute decimated index of decimated[0]
  let nextFrame = 0; // absolute decimated index where the next frame starts
  let origin = null; // absolute input index of decimated sample 0
  let expected = null; // input index the next push should start at

  const reset = (startSample) => {
    rawLength = 0;
    rawStart = startSample;
    nextDecimated = 0;
    decimatedLength = 0;
    decimatedStart = 0;
    nextFrame = 0;
    origin = startSample;
  };

  // The decimated search finds the right dip; measuring it again on the
  // full-rate input, where a high voice's period spans `factor` times as many
  // samples, keeps sopranos as precise as basses. Returns the period in
  // input samples.
  const refine = (period, start) => {
    if (factor === 1) return period;
    const centre = Math.round(period * factor);
    const size = windowSize * factor;
    const low = Math.max(2, centre - factor);
    const high = centre + factor;
    if (start < 0 || start + size + high + 1 >= rawLength) return period * factor;
    const values = [];
    for (let tau = low - 1; tau <= high + 1; tau++) {
      let sum = 0;
      for (let j = 0; j < size; j++) {
        const d = raw[start + j] - raw[start + j + tau];
        sum += d * d;
      }
      values.push(sum);
    }
    let best = 1;
    for (let i = 2; i < values.length - 1; i++) {
      if (values[i] < values[best]) best = i;
    }
    const a = values[best - 1];
    const b = values[best];
    const c = values[best + 1];
    const denominator = a - 2 * b + c;
    const tau = low - 1 + best;
    return denominator > 0 ? tau + (0.5 * (a - c)) / denominator : tau;
  };

  const analyse = (offset, rawOffset) => {
    const x = decimated;
    let energy = 0;
    for (let j = 0; j < windowSize; j++) energy += x[offset + j] * x[offset + j];
    const rms = Math.sqrt(energy / windowSize);

    for (let tau = 1; tau <= tauMax; tau++) {
      let sum = 0;
      for (let j = 0; j < windowSize; j++) {
        const d = x[offset + j] - x[offset + j + tau];
        sum += d * d;
      }
      diff[tau] = sum;
    }
    let running = 0;
    cmnd[0] = 1;
    for (let tau = 1; tau <= tauMax; tau++) {
      running += diff[tau];
      cmnd[tau] = running > 0 ? (diff[tau] * tau) / running : 1;
    }

    // First dip under the threshold (then down to its bottom); failing that
    // the deepest dip. Taking the first dip avoids octave-down errors.
    let best = -1;
    for (let tau = tauMin; tau <= tauMax; tau++) {
      if (cmnd[tau] < YIN_THRESHOLD) {
        while (tau + 1 <= tauMax && cmnd[tau + 1] < cmnd[tau]) tau++;
        best = tau;
        break;
      }
    }
    if (best < 0) {
      best = tauMin;
      for (let tau = tauMin + 1; tau <= tauMax; tau++) {
        if (cmnd[tau] < cmnd[best]) best = tau;
      }
    }

    // Parabolic interpolation around the dip for sub-sample precision
    let period = best;
    if (best > 1 && best < tauMax) {
      const a = cmnd[best - 1];
      const b = cmnd[best];
      const c = cmnd[best + 1];
      const denominator = a - 2 * b + c;
      if (denominator > 0) period = best + (0.5 * (a - c)) / denominator;
    }
    return { f0: sampleRate / refine(period, rawOffset), clarity: Math.max(0, 1 - cmnd[best]), rms };
  };

  /**
   * Add input starting at absolute sample index `startSample`. A small gap
   * since the previous push (the audio thread dropping a block now and
   * then) is filled with silence, which keeps every frame's time exact and
   * costs a frame or two; a long gap or an overlap restarts the stream.
   * Returns the frames that became complete.
   */
  const push = (samples, startSample) => {
    const missing = expected === null ? -1 : startSample - expected;
    if (missing > 0 && missing <= maxFill) {
      raw = append(raw, rawLength, new Float32Array(missing));
      rawLength += missing;
    } else if (missing !== 0) {
      reset(startSample);
    }
    expected = startSample + samples.length;
    raw = append(raw, rawLength, samples);
    rawLength += samples.length;

    // Low-pass and keep every factor-th sample; decimated sample k is
    // centred on input sample origin + k * factor
    for (;;) {
      const centre = origin + nextDecimated * factor;
      if (centre + half >= rawStart + rawLength) break;
      let acc = 0;
      for (let m = 0; m < taps.length; m++) {
        const index = centre + m - half - rawStart;
        if (index >= 0) acc += taps[m] * raw[index];
      }
      if (decimatedLength >= decimated.length) decimated = append(decimated, decimatedLength, Float32Array.of(acc));
      else decimated[decimatedLength] = acc;
      decimatedLength++;
      nextDecimated++;
    }

    const frames = [];
    while (nextFrame + span <= decimatedStart + decimatedLength) {
      const result = analyse(nextFrame - decimatedStart, origin + nextFrame * factor - rawStart);
      result.sample = origin + Math.round((nextFrame + span / 2) * factor);
      frames.push(result);
      nextFrame += hop;
    }

    // Drop input that neither the filter nor the next frame's refinement needs
    const keepFrom = Math.min(origin + nextDecimated * factor - half, origin + nextFrame * factor);
    const drop = Math.min(rawLength, Math.max(0, keepFrom - rawStart));
    if (drop > 0) {
      raw.copyWithin(0, drop, rawLength);
      rawLength -= drop;
      rawStart += drop;
    }

    // Drop decimated samples no frame needs any more
    const decimatedDrop = Math.min(decimatedLength, Math.max(0, nextFrame - decimatedStart));
    if (decimatedDrop > 0) {
      decimated.copyWithin(0, decimatedDrop, decimatedLength);
      decimatedLength -= decimatedDrop;
      decimatedStart += decimatedDrop;
    }
    return frames;
  };

  return { push, hopSeconds: (hop * factor) / sampleRate };
}
